// client/src/pages/Blogs.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Slider from 'react-slick';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

function getMediaUrl(path) {
  if (!path) return '/assets/images/placeholder.png';
  return `${process.env.REACT_APP_API_URL}/${path}`;
}

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);

  const [searchParams] = useSearchParams();
  const [sortOption, setSortOption] = useState('');
  const [searchText, setSearchText] = useState('');

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/blogs`)
      .then(res => {
        setBlogs(res.data);
      })
      .catch(err => console.error('Error fetching blogs:', err));
  }, []);

  // Filter + sort whenever blogs, search, or sort changes
  useEffect(() => {
    const search = searchParams.get('search') || '';
    setSearchText(search);

    let result = [...blogs];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(b => b.title.toLowerCase().includes(s));
    }
    switch (sortOption) {
      case 'date-newest':
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'date-oldest':
        result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'title-asc':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-desc':
        result.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        // No sort
        break;
    }
    setDisplayedBlogs(result);
  }, [blogs, sortOption, searchParams]);

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 mt-16">
      <h1 className="text-3xl font-bold mb-6">Our Blogs</h1>

      {/* Sorting + Search Info */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        {searchText && (
          <div className="mb-2 md:mb-0">
            <span className="inline-block bg-gray-200 px-2 py-1 rounded">
              Search: {searchText}
            </span>
          </div>
        )}
        <div className="flex items-center space-x-2">
          <label className="font-medium">Sort by:</label>
          <select
            className="border p-2"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="">None</option>
            <option value="date-newest">Date: Newest First</option>
            <option value="date-oldest">Date: Oldest First</option>
            <option value="title-asc">Title: A to Z</option>
            <option value="title-desc">Title: Z to A</option>
          </select>
        </div>
      </div>

      {displayedBlogs.length === 0 ? (
        <p>No blogs found.</p>
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } }
          }}
        >
          {displayedBlogs.map(blog => {
            const media = [...(blog.images || []), ...(blog.videos || [])];
            return (
              <motion.div
                key={blog._id}
                className="bg-white rounded shadow-custom overflow-hidden flex flex-col"
                variants={cardVariants}
              >
                <div className="w-full h-48">
                  {media.length > 1 ? (
                    <Slider {...sliderSettings}>
                      {media.map((m, idx) => {
                        const isVideo = /\.(mp4|mov|avi|webm)$/i.test(m);
                        const mediaUrl = getMediaUrl(m);
                        return (
                          <div key={idx}>
                            {isVideo ? (
                              <video
                                src={mediaUrl}
                                className="w-full h-48 object-cover"
                                controls
                              />
                            ) : (
                              <img
                                src={mediaUrl}
                                alt={`blog-media-${idx}`}
                                className="w-full h-48 object-cover"
                              />
                            )}
                          </div>
                        );
                      })}
                    </Slider>
                  ) : media.length === 1 ? (
                    /\.(mp4|mov|avi|webm)$/i.test(media[0]) ? (
                      <video
                        src={getMediaUrl(media[0])}
                        className="w-full h-48 object-cover"
                        controls
                      />
                    ) : (
                      <img
                        src={getMediaUrl(media[0])}
                        alt={blog.title}
                        className="w-full h-48 object-cover"
                      />
                    )
                  ) : (
                    <img
                      src="/assets/images/placeholder.png"
                      alt={blog.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                </div>

                <div className="p-4 flex-grow">
                  <h2 className="text-xl font-semibold mb-2">{blog.title}</h2>
                  {/* Optionally show small snippet of content */}
                </div>

                <div className="p-4">
                  <Link
                    to={`/blogs/${blog._id}`}
                    className="bg-secondaryGold text-black px-4 py-2 rounded-full 
                               hover:bg-yellow-400 transition"
                  >
                    Read More
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
